import { useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { CopyValue } from 'components/CopyValue';
import { StateHistory } from 'components/DetailsView/StateHistory';
import { Modal } from 'components/Modal';
import { PermissionGate } from 'components/PermissionGate';
import { confirmPopup } from 'components/Popups/Confirm';
import { TotemDatePicker } from 'components/TotemDatePicker';
import { TotemSelect } from 'components/TotemSelect';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemTextArea } from 'components/TotemTextArea';
import { UltraSelect } from 'components/UltraSelect';

import {
    TaskState,
    TaskType,
    TotadminPermission,
    useCreateTaskMutation,
    useUpdateTaskMutation,
} from 'data/__generated__';
import { siteSource } from 'data/entity-sources/sources';
import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';

import { TASK_STATE_LABELS, TASK_STATES_CLOSED, TASK_TYPE_LABELS, type TourTask } from '../helpers';
import { TaskPhotos } from './TaskStop';
import { TaskTickets } from './TaskTickets';
import { TaskStatusTag } from './TourStatusTag';

const TASK_TYPE_OPTIONS = (Object.keys(TASK_TYPE_LABELS) as TaskType[]).map((type) => ({
    value: type,
    label: TASK_TYPE_LABELS[type],
}));

const TASK_DEFAULT_INSTRUCTIONS: Record<TaskType, string[]> = {
    [TaskType.Audit]: [
        'Nettoyer la machine : vitres, intérieur et abords',
        'Retirer les produits périmés ou abîmés',
        'Remettre les produits et les étiquettes prix en place selon le planogramme',
    ],
    [TaskType.SpecificWork]: [],
    [TaskType.BoxPickup]: ['Récupérer toutes les caisses vides du site'],
    [TaskType.Ticket]: ['Intervenir sur la panne décrite dans le ticket'],
    [TaskType.Other]: [],
};

const TASK_STATE_OPTIONS = (Object.keys(TASK_STATE_LABELS) as TaskState[]).map((state) => ({
    value: state,
    label: TASK_STATE_LABELS[state],
}));

export const TaskPopup = ({
    task,
    birthdayDate,
    onClose,
    onSaved,
}: {
    task: TourTask | null;
    birthdayDate: string;
    onClose: () => void;
    onSaved: () => void;
}) => {
    const [type, setType] = useState<TaskType | null>(task?.type ?? null);
    const [siteId, setSiteId] = useState<string | null>(task?.siteId ?? null);
    const [description, setDescription] = useState(task?.description ?? '');
    const [instructions, setInstructions] = useState(task?.instructions.map(({ label }) => label).join('\n') ?? '');
    const [state, setState] = useState<TaskState | null>(task?.state ?? null);
    const [comment, setComment] = useState(task?.driversComment ?? '');
    const [date, setDate] = useState(() => dateFromString(birthdayDate));
    const day = formatDateAsAnniversary({ dateTime: date });

    const [createTask, { loading: creating }] = useCreateTaskMutation();
    const [updateTask, { loading: updating }] = useUpdateTaskMutation();

    const canSave =
        type != null &&
        siteId != null &&
        description.trim().length > 0 &&
        instructions.split('\n').some((line) => line.trim());
    const isClosed = task != null && TASK_STATES_CLOSED.includes(task.state);

    // At creation, the type fills its default instructions — unless the ops already wrote their own.
    const changeType = (nextType: TaskType | null): void => {
        const previousDefaults = type ? TASK_DEFAULT_INSTRUCTIONS[type].join('\n') : '';
        if (!task && (!instructions.trim() || instructions === previousDefaults)) {
            setInstructions(nextType ? TASK_DEFAULT_INSTRUCTIONS[nextType].join('\n') : '');
        }
        setType(nextType);
    };

    const save = async (): Promise<void> => {
        if (!type || !siteId) return;
        const lines = instructions.split('\n');
        const nextState = state ?? task?.state;
        const label = nextState && TASK_STATE_LABELS[nextState];
        const isStateChange = task != null && nextState !== task.state;
        if (
            isStateChange &&
            !(await confirmPopup({ title: 'Confirmer', message: `Passer la tâche à « ${label} » ?` }))
        ) {
            return;
        }

        try {
            const result =
                task && nextState
                    ? (
                          await updateTask({
                              variables: {
                                  taskId: task._id,
                                  input: {
                                      birthdayDate: day,
                                      type,
                                      description,
                                      instructions: lines,
                                      state: nextState,
                                      comment,
                                  },
                              },
                          })
                      ).data?.updateTask
                    : (
                          await createTask({
                              variables: {
                                  input: { type, birthdayDate: day, siteId, description, instructions: lines },
                              },
                          })
                      ).data?.createTask;
            if (!result?.success) {
                toast.error(result?.error ?? 'Une erreur est survenue');
                return;
            }
            toast.success(
                !task
                    ? 'Tâche créée, à placer dans une tournée'
                    : isStateChange
                      ? `Tâche passée à « ${label} »`
                      : day !== birthdayDate
                        ? `Tâche déplacée au ${formatDateAsAnniversary({ dateTime: date })}`
                        : 'Tâche mise à jour',
            );
            onSaved();
            onClose();
        } catch (error) {
            toast.error(error instanceof Error ? error.message : String(error));
        }
    };

    const datePicker = (
        <TotemDatePicker
            label="Date"
            selected={date}
            onChange={(value) => value && setDate(value)}
            isDisabled={isClosed}
        />
    );

    const fields = (
        <Column>
            <TotemSelect
                label="Type"
                placeholder="Contrôle site, travaux…"
                value={type}
                options={TASK_TYPE_OPTIONS}
                onChange={changeType}
                disabled={isClosed}
                dataTest="task-type"
            />
            {task ? (
                <ReadOnly>
                    <b>Site</b> {task.site?.name ?? task.siteId}
                </ReadOnly>
            ) : (
                <UltraSelect source={siteSource} label="Choisir un site" value={siteId} onChange={setSiteId} />
            )}
            {!task && datePicker}
            <TotemTextArea
                label="Description"
                placeholder="Pourquoi le livreur y va et, si possible, la machine concernée"
                value={description}
                onChange={setDescription}
                rows={2}
                disabled={isClosed}
                dataTest="task-description"
            />
            <TotemTextArea
                label="Instructions, une par ligne : chacune demandera une photo avant et une photo après"
                placeholder={'Vérifier la propreté du frigo\nRetirer les produits périmés'}
                value={instructions}
                onChange={setInstructions}
                rows={3}
                disabled={isClosed}
                dataTest="task-instructions"
            />
            {task && (
                <TotemTextArea
                    label="Commentaire"
                    value={comment}
                    onChange={setComment}
                    rows={2}
                    disabled={isClosed}
                    dataTest="task-comment"
                />
            )}
            {task?.instructions.some(({ controlPhotos }) => controlPhotos.length) && (
                <ReadOnly>
                    <b>Photos</b>
                    <TaskPhotos photos={task.instructions.flatMap(({ controlPhotos }) => controlPhotos)} />
                </ReadOnly>
            )}
            {siteId && (
                <PermissionGate requiredPermissions={[TotadminPermission.TicketsRead]}>
                    <ReadOnly>
                        <b>Tickets du site</b>
                        <TaskTickets siteId={siteId} day={day} />
                    </ReadOnly>
                </PermissionGate>
            )}
        </Column>
    );

    return (
        <Modal
            title={task ? `Tâche · ${TASK_STATE_LABELS[task.state]}` : 'Nouvelle tâche'}
            isOpen
            onClose={onClose}
            className={task ? 'sm:max-w-[940px]' : 'sm:max-w-[550px]'}
        >
            <Body>
                {task ? (
                    <Columns>
                        <Column>
                            <CopyValue label="ID de la tâche" value={task._id} />
                            {datePicker}
                            <TotemSelect<TaskState>
                                label="Statut"
                                value={state}
                                options={TASK_STATE_OPTIONS}
                                onChange={setState}
                                dataTest="task-state"
                            />
                            <StateHistory
                                label="Historique"
                                records={task.stateHistory.map((record) => ({
                                    ...record,
                                    state: <TaskStatusTag state={record.state} />,
                                }))}
                            />
                        </Column>
                        {fields}
                    </Columns>
                ) : (
                    fields
                )}
            </Body>
            <Actions>
                <TotemPrimaryButton
                    onClick={() => void save()}
                    disabled={!canSave || creating || updating}
                    data-test="task-save"
                >
                    {task ? 'Enregistrer' : 'Créer la tâche'}
                </TotemPrimaryButton>
            </Actions>
        </Modal>
    );
};

const Body = styled.div`
    max-height: calc(100vh - 180px);
    overflow-y: auto;
`;

const Columns = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr);
    gap: 20px;

    @media (max-width: 640px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
`;

const ReadOnly = styled.div`
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};

    b {
        display: block;
        font-size: 12px;
        color: ${({ theme }) => theme.infoTextColor};
    }
`;

const Actions = styled.div`
    display: flex;
    justify-content: center;
`;
